import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  approveDeviceAuthorization,
  denyDeviceAuthorization,
  lookUpDeviceAuthorization,
} from "../../../api/connectorsApi";
import { isAbortError } from "../../../api/http/httpClient";
import { reportUiError, toUiError } from "../../../errors/uiError";
import { initialPairingState, transitionPairing, type PairingDecision, type PairingState } from "./pairingFlow";
import type { UserCode } from "./userCode";

export interface PairingFlowHandle {
  readonly state: PairingState;
  readonly submitCode: (userCode: UserCode) => void;
  readonly decide: (decision: PairingDecision) => void;
  readonly enterOtherCode: () => void;
}

/** Runs the pairing flow's requests: the lookup whenever a code is submitted, and one decision. */
export function usePairingFlow(codeFromAddress: string | null): PairingFlowHandle {
  const [state, dispatch] = useReducer(transitionPairing, codeFromAddress, initialPairingState);
  // Guards against a second decision before React has rendered "deciding" (a double click).
  const decidingRef = useRef(false);

  const lookingUpCode = state.status === "lookingUp" ? state.userCode : null;
  useEffect(() => {
    if (lookingUpCode === null) {
      return;
    }
    const controller = new AbortController();
    lookUpDeviceAuthorization(lookingUpCode, controller.signal).then(
      (authorization) => {
        if (!controller.signal.aborted) {
          dispatch({ type: "lookupSucceeded", authorization });
        }
      },
      (caught: unknown) => {
        if (controller.signal.aborted || isAbortError(caught)) {
          return;
        }
        const error = toUiError(caught);
        reportUiError(error);
        dispatch({ type: "lookupFailed", error });
      },
    );
    return () => {
      controller.abort();
    };
  }, [lookingUpCode]);

  const openExpiresAt =
    state.status === "awaitingDecision" || state.status === "decisionFailed" ? state.authorization.expiresAt : null;
  useEffect(() => {
    if (openExpiresAt === null) {
      return;
    }
    const timer = window.setTimeout(() => {
      dispatch({ type: "codeExpired" });
    }, Math.max(0, Date.parse(openExpiresAt) - Date.now()));
    return () => {
      window.clearTimeout(timer);
    };
  }, [openExpiresAt]);

  const decidableCode =
    state.status === "awaitingDecision" || state.status === "decisionFailed" ? state.authorization.userCode : null;
  const decide = useCallback(
    (decision: PairingDecision) => {
      if (decidableCode === null || decidingRef.current) {
        return;
      }
      decidingRef.current = true;
      dispatch({ type: "decisionStarted", decision });
      const request =
        decision === "approve"
          ? approveDeviceAuthorization(decidableCode).then((connector) => {
              dispatch({ type: "approvalSucceeded", connector });
            })
          : denyDeviceAuthorization(decidableCode).then(() => {
              dispatch({ type: "denialSucceeded" });
            });
      request
        .catch((caught: unknown) => {
          const error = toUiError(caught);
          reportUiError(error);
          dispatch({ type: "decisionFailed", error });
        })
        .finally(() => {
          decidingRef.current = false;
        });
    },
    [decidableCode],
  );

  const submitCode = useCallback((userCode: UserCode) => {
    dispatch({ type: "codeSubmitted", userCode });
  }, []);

  const enterOtherCode = useCallback(() => {
    dispatch({ type: "otherCodeRequested" });
  }, []);

  return { state, submitCode, decide, enterOtherCode };
}
